import IVStepper from 'components/common/IVStepper';
import Page from 'components/Page';
import React from 'react';
import {useTranslation} from 'react-i18next';
import {Outlet, useLocation, useMatch, useNavigate} from 'react-router-dom';
import {device} from '@guestapp/ui/src/utils/device';
import {usePath, useScreenResize} from '../../hooks';
import {PATHS} from 'Routes';
import {useOpenModals} from 'context/openModals';

import ChekinProSettings from '../../ChekinProSettings';
import {SDK_MODE} from 'utils/embedded';
import DiscardLastPhotoModal from 'components/common/DiscardLastPhotoModal';
import {DiscardLastPhotoModalRef} from 'components/common/DiscardLastPhotoModal/DiscardLastPhotoModal';
import {TreatImagesInfoModal, IdentityContainer} from './styled';
import {VerificationCompletedProvider} from 'components/VerificationCompletedModal';

function IdentityVerification() {
  const {t} = useTranslation();
  const {isMatch: isMobileMode} = useScreenResize(device.tabletSubHeader);
  const discardLastPhotoModalRef = React.useRef<DiscardLastPhotoModalRef>(null);
  const {checkIfOpenDiscardModalAndRedirect} = useOpenModals();
  const {pathname} = useLocation();
  const navigate = useNavigate();
  const {getFullPath} = usePath();

  const isRootVerification = useMatch('/:token?/verification/:id?');
  const isStartView = useMatch('/:token?/verification');
  const isScanQR = useMatch('/:token?/verification/:id?/scan-qr-code');
  const isSelfieInstructionsView = useMatch(
    '/:token?/verification/:id?/selfie-instructions',
  );
  const isContactsVerificationView = useMatch('/:token?/verification/contacts');
  const isVerificationCompleted = useMatch(
    '/:token?/verification/verification-completed',
  );
  const isSelfieView = useMatch('/:token?/verification/selfie');
  const areSelfieViews = isSelfieView || isSelfieInstructionsView;
  const shouldHideStepper =
    isContactsVerificationView ||
    (isRootVerification && !areSelfieViews) ||
    isScanQR ||
    isVerificationCompleted;

  const isFrontSideView = useMatch('/:token?/verification/front/take');
  const isBackSideView = useMatch('/:token?/verification/back/take');
  const isBackSideUploadTypeSelection = useMatch(
    '/:token?/verification/uploading-type/back',
  );
  const isBackOrSelfieView =
    pathname.includes(PATHS.verification.selfie) || isBackSideUploadTypeSelection;

  const openDiscardIVLastPhotoModal = () =>
    discardLastPhotoModalRef.current?.open(PATHS.verification.main);

  const onBackButtonClick = () =>
    isBackOrSelfieView
      ? openDiscardIVLastPhotoModal()
      : isRootVerification
      ? navigate(getFullPath(PATHS.home), {replace: true})
      : checkIfOpenDiscardModalAndRedirect('-1');

  const isTakeView =
    isMobileMode &&
    (Boolean(isFrontSideView) || Boolean(isBackSideView) || Boolean(isSelfieView));

  const isSubHeaderVisible =
    ChekinProSettings.mode === SDK_MODE.onlyIV
      ? !isStartView && !isTakeView
      : !isTakeView;

  return (
    <VerificationCompletedProvider>
      <IdentityContainer>
        <Page
          isSubHeaderVisible={isSubHeaderVisible}
          isBackButtonDisabled={
            isMobileMode ||
            Boolean(ChekinProSettings.mode === SDK_MODE.onlyIV && isStartView)
          }
          title={t('identity_verification')}
          onBackButtonClick={onBackButtonClick}
          navBarTitle={
            isMobileMode ? t('identity_verification') : t('guest_registration')
          }
          className="iv-flow-layout"
          mainHeader={ChekinProSettings.mode === SDK_MODE.onlyIV ? false : !isMobileMode}
        >
          <TreatImagesInfoModal />
          {!shouldHideStepper /*|| isKiosko*/ && (
            <IVStepper onClickBackMobile={onBackButtonClick} />
          )}
          <DiscardLastPhotoModal ref={discardLastPhotoModalRef} />
          <Outlet />
        </Page>
      </IdentityContainer>
    </VerificationCompletedProvider>
  );
}

export {IdentityVerification};
